/**
 * Recharts render layer pro Graph2. Pure — bere data + interaction state + bounds
 * jako props, kreslí. Žádný fetch, žádný local state (kromě tooltip cache).
 *
 * Klíčové změny vůči původnímu Graph2:
 *   - `<Line type="stepBefore">` pro BOOLEAN topicy (místo synthetic step pointů v DB)
 *   - `<Area>` band (min..max) navrch `<Line avg>` pro BUCKETED resolution
 *   - dynamický `yAxisWidth` měřený přes module-scoped cached canvas
 *   - midnight reference lines memoizované
 *   - žádný `Math.min(...spread)` — minMax přichází z Graph2Data
 *
 * Gesture overhaul (viz docs/plans/GRAPH2-GESTURE-OVERHAUL.md):
 *   - listenery váže `useGraph2Interaction` přes `target: containerRef` (žádný `{...handlers}`)
 *   - `touch-action` podmíněný (`pan-y` při interakci → svislý tah scrolluje stránku)
 *   - tooltip: recharts native `<Tooltip>` pro jemný pointer (hover), plovoucí clamped
 *     karta pro coarse pointer (touch tap/scrub) — bez UA `isMobile`.
 */

import React, { useCallback, useMemo, type RefObject } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { eachDayOfInterval, startOfDay } from "date-fns";
import { formatDate, formatDecimal } from "@globals/format";
import { mathRound } from "@globals/numbers";
import { colors, suspiciousColor } from "@client/constants";
import { useDark } from "@client/hooks/useDark";
import { type ChartResolutionKey } from "@client/lib/chartBucketing";
import type { ChartPoint } from "@client/store/chart";
import { Graph2Tooltip } from "./Graph2Tooltip";
import type { Bounds } from "./Graph2Interaction";
import type { ChartRow } from "./Graph2Data";

const TICK_FONT_SIZE = 12;
const TICK_FONT_FAMILY = "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif";
const CHART_MARGIN = { top: 0, right: 0, bottom: 0, left: 0 };

/** Coarse pointer = dotykové zařízení (primární vstup). Nahrazuje UA `isMobile` pro
 *  rozhodnutí render-mode tooltipu (jemný pointer → recharts hover). */
const prefersCoarsePointer = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(pointer: coarse)").matches;

/** Module-scoped canvas pro text-width measurements (sdílený mezi všemi Graph2 instancemi). */
let _measureCanvas: HTMLCanvasElement | null = null;
let _measureCtx: CanvasRenderingContext2D | null = null;
const measureTextWidth = (text: string, font: string): number => {
  if (typeof document === "undefined") return Math.max(8, text.length * (TICK_FONT_SIZE * 0.6));
  if (!_measureCanvas) {
    _measureCanvas = document.createElement("canvas");
    _measureCtx = _measureCanvas.getContext("2d");
  }
  if (!_measureCtx) return Math.max(8, text.length * (TICK_FONT_SIZE * 0.6));
  _measureCtx.font = font;
  return _measureCtx.measureText(text).width;
};

const generateTimeTicks = (min: number, max: number): number[] => {
  const range = max - min;
  let interval: number;
  if (range <= 60_000 * 1.5) interval = 1_000;
  else if (range <= 600_000 * 1.5) interval = 60_000;
  else if (range <= 3_600_000 * 1.5) interval = 600_000;
  else if (range <= 86_400_000 * 1.52) interval = 3_600_000;
  else if (range <= 604_800_000) interval = 86_400_000;
  else interval = 604_800_000;
  const firstTick = Math.ceil(min / interval) * interval;
  const ticks: number[] = [];
  for (let t = firstTick; t <= max; t += interval) ticks.push(t);
  return ticks;
};

const formatTickByRange = (timestamp: number, rangeMs: number): string => {
  if (rangeMs <= 60_000) return formatDate(new Date(timestamp), "HH:mm:ss");
  if (rangeMs <= 86_400_000 * 2) return formatDate(new Date(timestamp), "HH:mm");
  return formatDate(new Date(timestamp), "d. M. yyyy");
};

/** "Nice" krok osy (1/2/5 × 10^k) pro daný hrubý krok. */
const niceStep = (rough: number): number => {
  if (!(rough > 0)) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(rough)));
  const n = rough / pow;
  const nice = n < 1.5 ? 1 : n < 3 ? 2 : n < 7 ? 5 : 10;
  return nice * pow;
};

/**
 * Tiky osy Y jako násobky "nice" kroku, UVNITŘ datového rozsahu [min, max] (lo = ceil,
 * hi = floor → žádný headroom nad/pod daty). Když rozsah obsahuje nulu (min<0<max), `0`
 * je vždy mezi tiky (0 je násobek libovolného kroku) a doléhá na výraznou nulovou čáru.
 * Domain se drží těsný zvlášť (viz yDomain) — osa tak hugne kreslenou čáru.
 */
const niceTicks = (min: number, max: number, count: number): number[] => {
  if (min === max) return [min];
  const step = niceStep((max - min) / Math.max(1, count - 1));
  const lo = Math.ceil(min / step) * step;
  const hi = Math.floor(max / step) * step;
  const ticks: number[] = [];
  for (let t = lo; t <= hi + step * 1e-6; t += step) ticks.push(t);
  return ticks;
};

export interface Graph2RenderProps {
  containerRef: RefObject<HTMLDivElement | null>;
  bounds: Bounds;
  containerWidth: number;
  containerHeight: number;
  isPanning: boolean;
  /** True když lze graf ovládat gesty (= !editMode). Řídí `touch-action` (mobil passthrough). */
  interactive: boolean;
  /** Desktop "lock" — myš panuje jen když true. Řídí cursor (touch ho ignoruje). */
  chartLock: boolean;
  showTooltipAt: { x: number; y: number; timestamp: number } | null;
  topicIds: string[];
  hidden?: boolean[];
  className?: string;
  style?: React.CSSProperties;

  chartData: ChartRow[];
  /** Per-topic sorted points pro tooltip per-topic interpolaci. */
  series: Map<string, ChartPoint[]>;
  minMax: { minV: number; maxV: number; minT: number; maxT: number };
  topics: Record<string, MQTTValue["topic"]>;
  suspicious: Record<string, boolean>;
  resolution: Map<string, ChartResolutionKey>;
  /** True jen dokud nedorazil PRVNÍ batch dat — pak permanentně false. */
  showInitialSpinner?: boolean;
}

const Graph2Render: React.FC<Graph2RenderProps> = ({
  containerRef,
  bounds,
  containerWidth,
  containerHeight,
  isPanning,
  interactive,
  chartLock,
  showTooltipAt,
  topicIds,
  hidden,
  className = "",
  style,
  chartData,
  series,
  minMax,
  topics,
  suspicious,
  resolution,
  showInitialSpinner = false,
}) => {
  void containerWidth;
  void containerHeight;
  const { dark } = useDark();
  const coarsePointer = useMemo(() => prefersCoarsePointer(), []);

  const decimals = topics[topicIds[0]]?.decimals ?? 1;

  /**
   * Dvoustavový graf. Odvozuje se z typu topicu, ne z propy `boolean` z konfigurace
   * widgetu — ta říká, jak se to má kreslit, kdežto tohle je, co ta veličina doopravdy je.
   */
  const allBool = topicIds.length > 0 && topicIds.every((t) => topics[t]?.valueType === "BOOLEAN");

  const axisTick = useMemo(
    () => ({ fill: dark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)", fontSize: TICK_FONT_SIZE, fontFamily: TICK_FONT_FAMILY }),
    [dark]
  );

  // Nice-step tiky UVNITŘ rozsahu → 0 je tik když data spaní nulu, bez headroomu nad daty.
  const yAxisTicks = useMemo(() => {
    // Ano/ne má přesně dvě smysluplné hodnoty. „Nice" krok by z rozsahu 0..1 udělal
    // 0 / 0,2 / 0,4 …, což u schodové čáry nic neznamená.
    if (allBool) return [0, 1];
    if (minMax.minV === Number.MAX_SAFE_INTEGER) return [0, 1];
    if (minMax.maxV - minMax.minV === 0) return [mathRound(minMax.minV, decimals)];
    const raw = niceTicks(minMax.minV, minMax.maxV, 6);
    if (raw.length === 0) return [mathRound(minMax.minV, decimals), mathRound(minMax.maxV, decimals)];
    return Array.from(new Set(raw.map((t) => mathRound(t, decimals))));
  }, [allBool, minMax.minV, minMax.maxV, decimals]);

  // Domain TĚSNÝ (hugne data, 2 % padding) — žádné zaokrouhlení ven, žádný zbytečný headroom.
  const yDomain = useMemo((): [number, number] => {
    // Pevný rozsah s malým přesahem — bez něj se čára v poloze 1 kreslí přesně na horní
    // hraně a půlka tahu se ořízne. Přesah se nesmí zaokrouhlovat podle `decimals`.
    if (allBool) return [-0.06, 1.06];
    if (minMax.minV === Number.MAX_SAFE_INTEGER) return [0, 1];
    const range = minMax.maxV - minMax.minV;
    if (range === 0) return [minMax.minV - 1, minMax.maxV + 1];
    const pad = range * 0.02;
    return [mathRound(minMax.minV - pad, decimals), mathRound(minMax.maxV + pad, decimals)];
  }, [allBool, minMax.minV, minMax.maxV, decimals]);

  // Nula leží v rozsahu (kladné i záporné hodnoty) → výrazná nulová čára.
  const showZeroLine = minMax.minV !== Number.MAX_SAFE_INTEGER && minMax.minV < 0 && minMax.maxV > 0;

  const formatYAxisTick = useCallback(
    // U ano/ne se `decimals` topicu ignoruje — „0,0" a „1,0" jsou u dvou stavů jen šum.
    (val: number) =>
      allBool
        ? String(Math.round(val))
        : decimals > 0
          ? formatDecimal(mathRound(val, decimals), decimals)
          : String(mathRound(val, 0)),
    [allBool, decimals]
  );

  const yAxisWidth = useMemo(() => {
    const font = `${TICK_FONT_SIZE}px ${TICK_FONT_FAMILY}`;
    let maxWidth = 0;
    for (const tick of yAxisTicks) {
      const w = measureTextWidth(formatYAxisTick(tick), font);
      if (w > maxWidth) maxWidth = w;
    }
    return Math.ceil(maxWidth) + 12;
  }, [yAxisTicks, formatYAxisTick]);

  const timeTicks = useMemo(() => generateTimeTicks(bounds.min, bounds.max), [bounds.min, bounds.max]);
  const formatXAxis = useCallback(
    (timestamp: number) => formatTickByRange(timestamp, bounds.max - bounds.min),
    [bounds.min, bounds.max]
  );

  const midnightAnnotations = useMemo(() => {
    const start = startOfDay(new Date(bounds.min));
    const end = startOfDay(new Date(bounds.max));
    const midnights = eachDayOfInterval({ start, end });
    return midnights.map((date) => (
      <ReferenceLine key={date.getTime()} x={date.getTime()} stroke="rgba(120, 120, 120, 0.35)" strokeWidth={1} />
    ));
  }, [bounds.min, bounds.max]);

  const tooltipContent = useCallback(
    ({ active, label }: { active?: boolean; label?: string | number }) => (
      <Graph2Tooltip
        active={active}
        label={typeof label === "number" ? label : label !== undefined ? Number(label) : undefined}
        dark={dark}
        showTooltipAt={showTooltipAt}
        topicIds={topicIds}
        topics={topics}
        seriesByTopic={series}
        suspicious={suspicious}
      />
    ),
    [dark, showTooltipAt, topicIds, topics, series, suspicious]
  );

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        // pan-y: svislý 1-prst tah scrolluje stránku, horizontální/2-prst gesta zachytí graf.
        // auto: interakce vypnutá (editMode) → vše nechá prohlížeči.
        touchAction: interactive ? "pan-y" : "auto",
        overscrollBehavior: "contain",
        // Grab cursor jen když myš reálně panuje (desktop + chartLock). Touch kurzor ignoruje.
        cursor: interactive && chartLock ? (isPanning ? "grabbing" : "grab") : "default",
        ...style,
      }}
    >
      {showInitialSpinner && (
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              width: 24,
              height: 24,
              border: `2px solid ${dark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)"}`,
              borderTopColor: dark ? "#ffffff" : "#000000",
              borderRadius: "50%",
              animation: "graph2-spin 0.8s linear infinite",
            }}
          />
          <style>{`@keyframes graph2-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
      {chartData.length > 0 && (
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={CHART_MARGIN} accessibilityLayer={false}>
            <CartesianGrid vertical={false} stroke={dark ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.06)"} />
            <XAxis
              dataKey="timestamp"
              scale="time"
              type="number"
              domain={[bounds.min, bounds.max]}
              tickFormatter={formatXAxis}
              tick={axisTick}
              ticks={timeTicks}
              allowDataOverflow={true}
              height={22}
              tickMargin={2}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              domain={yDomain}
              ticks={yAxisTicks}
              tick={axisTick}
              tickFormatter={formatYAxisTick}
              allowDataOverflow={true}
              width={yAxisWidth}
              tickMargin={2}
              axisLine={false}
              tickLine={false}
            />
            {/* Jemný pointer (myš): recharts native hover tooltip. Coarse pointer (touch):
                plovoucí clamped karta řízená showTooltipAt (tap / long-press scrub). */}
            {!coarsePointer && <Tooltip content={tooltipContent} />}
            {showTooltipAt && tooltipContent({ active: true, label: showTooltipAt.timestamp })}
            {midnightAnnotations}
            {showZeroLine && (
              <ReferenceLine
                y={0}
                stroke={dark ? "rgba(255, 255, 255, 0.55)" : "rgba(0, 0, 0, 0.55)"}
                strokeWidth={1.5}
              />
            )}

            {topicIds.map((topic, index) => {
              const isBool = topics[topic]?.valueType === "BOOLEAN";
              const res = resolution.get(topic);
              const isBucketed = res !== undefined && res !== "raw";
              const color = suspicious[topic] ? suspiciousColor : colors[index % colors.length];
              return (
                <React.Fragment key={topic}>
                  {/* Bucketed FLOAT: min..max band za stredovou line. Recharts Area
                      potřebuje dataKey s 2-prvkovým polem [min, max] — to bychom
                      museli stavět v chartData. Pro jednoduchost (a backward compat
                      s recharts <Line>) tu prozatím vykreslujeme jen `avg` jako Line
                      a min/max band nepoužíváme. TODO: pokud chceme band, přidat do
                      ChartRow `${topic}_band: [min, max]` a renderovat Area zde. */}
                  {isBucketed && !isBool && (
                    <Area
                      dataKey={topic}
                      stroke="none"
                      fill={color}
                      fillOpacity={0}
                      isAnimationActive={false}
                      hide={hidden?.[index] ?? false}
                    />
                  )}
                  <Line
                    type={isBool ? "stepBefore" : "monotone"}
                    dataKey={topic}
                    name={topics[topic]?.nickname ?? topic}
                    stroke={color}
                    // Tenčí tah u ano/ne: schodová čára jsou samé svislice a při 2 px
                    // splývaly krátké pulzy (zvonek, dveře) do jedné tlusté šmouhy.
                    strokeWidth={isBool ? 1.25 : 2}
                    dot={false}
                    activeDot={{ r: 4 }}
                    isAnimationActive={false}
                    hide={hidden?.[index] ?? false}
                    connectNulls={true}
                  />
                </React.Fragment>
              );
            })}
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default React.memo(Graph2Render);
